/** Forgot-password email form on the login page. */
import { useId } from 'react';
import { useT } from '@/i18n/useT';
import { usePasswordReset } from '@/features/auth/usePasswordReset';

const LoginForgotPasswordForm = ({ email, setEmail, onBackToSignIn }) => {
  const t = useT();
  const reset = usePasswordReset({ email, setEmail });
  const emailErrorId = useId();
  const hasEmailError = Boolean(reset.fieldErrors.email);

  const handleBack = () => {
    reset.resetFormState();
    onBackToSignIn();
  };

  const sendDisabled =
    reset.loading || reset.cooldownActive || reset.sent;

  return (
    <div data-testid="login-forgot-password">
      {reset.serverErrorKey ? (
        <p className="login-server-error" role="alert">
          {t(reset.serverErrorKey, reset.serverErrorKey)}
        </p>
      ) : null}
      {reset.sent ? (
        <p className="login-reset-success" role="status">
          {t(
            'login.resetSuccess',
            'If this address exists, we will send a link.',
          )}
        </p>
      ) : null}
      <form className="login-form" onSubmit={reset.handleSubmit} noValidate>
        <div className="login-field">
          <label htmlFor="login-reset-email">
            {t('login.emailLabel', 'Email')}
          </label>
          <input
            id="login-reset-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={reset.email}
            onChange={(event) => reset.setEmail(event.target.value)}
            onBlur={reset.validateEmailBlur}
            aria-invalid={hasEmailError ? 'true' : 'false'}
            aria-describedby={hasEmailError ? emailErrorId : undefined}
          />
          {hasEmailError ? (
            <p id={emailErrorId} className="login-field-error" role="alert">
              {t(reset.fieldErrors.email, reset.fieldErrors.email)}
            </p>
          ) : null}
        </div>
        <button
          type="submit"
          className="login-submit-button"
          disabled={sendDisabled}
          data-testid="login-reset-submit"
        >
          {reset.loading
            ? t('login.submitting', 'Signing in…')
            : reset.cooldownActive
              ? t('login.resetCooldown', 'Send again in {seconds}s', {
                  seconds: reset.cooldownSecondsLeft,
                })
              : t('login.resetSend', 'Send reset link')}
        </button>
      </form>
      <button type="button" role="link" className="login-text-link" onClick={handleBack}>
        {t('login.resetBackToSignIn', 'Back to sign in')}
      </button>
    </div>
  );
};

export default LoginForgotPasswordForm;
